import React, { useCallback, useMemo } from 'react';
import { IntlShape, MessageDescriptor, useIntl } from 'react-intl';
import clsx from 'clsx';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { TypedMemo } from '../../../../common/types';
import { changedLyricLineCount, SongDraftField } from './draft/songPageDraft';
import { SongPageDraftState } from './draft/useSongPageDraft';
import { songPageMessages } from './songPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from './SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

const FIELD_LABELS: Record<SongDraftField, MessageDescriptor> = {
  title: songPageMessages.fieldTitleShort,
  artistName: songPageMessages.changedArtist,
  releaseType: songPageMessages.changedType,
  releaseDate: songPageMessages.changedDate,
  artwork: songPageMessages.changedCover,
  isrc: songPageMessages.changedIsrc,
  lyrics: songPageMessages.changedLyrics,
  ownership: songPageMessages.changedOwnership,
};

/** "Title, ISRC" (design b3), in the language of the app. */
export function changedFieldNames(intl: IntlShape, fields: SongDraftField[]): string {
  return intl.formatList(
    fields.map((field) => intl.formatMessage(FIELD_LABELS[field])),
    { type: 'conjunction' }
  );
}

interface SongSaveBarProps {
  form: SongPageDraftState;
}

/**
 * The save bar of the song page (owner decision 1; design b3, d2, e3, f5). It stays at the bottom
 * of the window while a field differs from the server, on every section. Save is off while a
 * field has an error. "All changes saved" shows for 1 s after a save.
 */
const SongSaveBarComponent: React.FC<SongSaveBarProps> = ({ form }) => {
  const intl = useIntl();
  const { changed, status, errorCount, failure, base, draft } = form;
  const save = useCallback(() => {
    form.save().catch(console.error);
  }, [form]);

  const detail = useMemo(() => {
    if (errorCount > 0) {
      return intl.formatMessage(songPageMessages.saveBarFixFirst, { count: errorCount });
    }
    if (changed.length === 1 && changed[0] === 'lyrics') {
      return intl.formatMessage(songPageMessages.saveBarLyrics, {
        count: changedLyricLineCount(base.lyrics, draft.lyrics),
      });
    }
    return changedFieldNames(intl, changed);
  }, [base.lyrics, changed, draft.lyrics, errorCount, intl]);

  if (status === 'saved' && changed.length === 0) {
    return (
      <div className={styles.saveBar} role="status" data-testid="song-save-bar">
        <span className={clsx(styles.saveDot, styles.saveDotOk)} />
        <span className={styles.grow}>{intl.formatMessage(songPageMessages.saveBarSaved)}</span>
      </div>
    );
  }
  if (changed.length === 0) {
    return null;
  }

  const isSaving = status === 'saving';
  const isFailed = status === 'failed' || status === 'partFailed';
  let text: React.ReactNode;
  if (isSaving) {
    text = intl.formatMessage(songPageMessages.saveBarSaving, { count: changed.length });
  } else if (isFailed) {
    const apiMessage = failure instanceof FYMAPIError && failure.isOperational ? failure.message : undefined;
    text = (
      <>
        {intl.formatMessage(status === 'failed' ? songPageMessages.saveBarFailed : songPageMessages.saveBarPartFailed)}
        {apiMessage !== undefined && <small> · {apiMessage}</small>}
      </>
    );
  } else {
    text = (
      <>
        {intl.formatMessage(songPageMessages.saveBarChanges, { count: changed.length })} <small>· {detail}</small>
      </>
    );
  }

  return (
    <div className={styles.saveBar} role="region" aria-live="polite" data-testid="song-save-bar">
      <span className={clsx(styles.saveDot, isFailed && styles.saveDotErr)} />
      <span className={styles.grow}>{text}</span>
      <button type="button" className={styles.saveButton} onClick={form.discard} disabled={isSaving}>
        {intl.formatMessage(songPageMessages.discard)}
      </button>
      <button
        type="button"
        className={clsx(styles.saveButton, styles.saveButtonPrimary)}
        onClick={save}
        disabled={isSaving || errorCount > 0}
        data-testid="song-save-button"
      >
        {intl.formatMessage(isFailed ? songPageMessages.tryAgain : songPageMessages.save)}
      </button>
    </div>
  );
};

export const SongSaveBar = TypedMemo(SongSaveBarComponent);
